import React, { useCallback } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { useTimeTrackingOptions } from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	TMPEpicPanelDetailsSkeleton,
	CMPEpicPanelDetailsSkeleton,
} from '@atlassian/jira-skeletons/src/ui/software/epic-panel/TMPEpicPanelDetailsSkeleton.tsx';
import { defaultTimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import CardButton from '../../../../../../common/ui/card-button/main.tsx';
import EstimateField from '../../../../../../common/ui/estimate-field/index.tsx';
import IssuesCount from '../../../../../../common/ui/issues-count/main.tsx';
import { Divider } from '../../../../../../common/ui/styled.tsx';
import { usePanelData } from '../../../../../../context/use-panel-data.tsx';
import DateField from './date-field/index.tsx';
import dateFieldMessages from './date-field/messages.tsx';
import { CardContentsErrorState } from './error-state/index.tsx';
import IssueKeyField from './issue-key-field/index.tsx';
import messages from './messages.tsx';
import type {
	EpicCardButtonsProps,
	EpicCardContentProps,
	EpicCardContentPropsCMP,
	EpicCardContentPropsTMP,
} from './types.tsx';

const EpicCardButtons = ({
	isActive,
	epicId,
	onViewDetailsClick,
	onCreateIssueClick,
}: EpicCardButtonsProps) => {
	const { isCompanyManaged } = usePanelData();
	const { formatMessage } = useIntl();

	const onCreateIssueInEpic = useCallback(() => {
		onCreateIssueClick(epicId);
	}, [epicId, onCreateIssueClick]);

	return (
		<>
			{isCompanyManaged ? (
				<Box xcss={cardButtonContainerStyles}>
					<CardButton
						analyticName="viewDetails"
						text={formatMessage(messages.viewDetails)}
						isActive={isActive}
						onClick={onViewDetailsClick}
					/>
					<CardButton
						analyticName="createIssueInEpic"
						text={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.createIssueIssueTermRefresh
								: messages.createIssue,
						)}
						isActive={isActive}
						onClick={onCreateIssueInEpic}
					/>
				</Box>
			) : (
				<CardButton
					isActive={isActive}
					text={formatMessage(messages.viewAllDetails)}
					onClick={onViewDetailsClick}
				/>
			)}
		</>
	);
};

// VISIBLE FOR TESTING
export const EpicCardContentsTMP = ({
	startDate,
	dueDate,
	isLoading,
	hasError,
	totalIssues,
	onRefetch,
}: EpicCardContentPropsTMP) => {
	const { formatMessage } = useIntl();

	if (isLoading) {
		return (
			<TMPEpicPanelDetailsSkeleton testId="software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.card-contents.skeleton" />
		);
	}

	if (hasError) {
		return <CardContentsErrorState onRefetch={onRefetch} />;
	}

	return (
		<>
			<div data-testid="software-backlog-panel.ui.epic.epic-panel.epic-card-list.epic-card.card-contents.total-issues">
				{totalIssues
					? null
					: formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.emptyIssueTermRefresh
								: messages.empty,
						)}
			</div>
			<DateField label={formatMessage(dateFieldMessages.startDate)} date={startDate} />
			<DateField label={formatMessage(dateFieldMessages.dueDate)} date={dueDate} />
		</>
	);
};

// VISIBLE FOR TESTING
export const EpicCardContentsCMP = ({
	isLoading,
	hasError,
	progress,
	estimation,
	epic,
	onDetailClick,
	onRefetch,
}: EpicCardContentPropsCMP) => {
	const { key: epicKey, id } = epic;

	const [timeTrackingOptions = defaultTimeTrackingOptions] = useTimeTrackingOptions();

	if (isLoading) {
		return <CMPEpicPanelDetailsSkeleton />;
	}
	if (hasError) {
		return <CardContentsErrorState onRefetch={onRefetch} />;
	}
	return (
		<>
			<IssueKeyField issueId={id} issueKey={epicKey} onDetailClick={onDetailClick} />
			<Divider />
			<IssuesCount totalIssues={progress.totalIssues} done={progress.status.done.issueCount} />
			{estimation && (
				<>
					<Box>
						<Divider />
					</Box>
					<EstimateField estimationInfo={estimation} timeTrackingOptions={timeTrackingOptions} />
				</>
			)}
		</>
	);
};

export const EpicCardContents = ({
	isActive,
	isLoading,
	hasError,
	progress,
	estimation,
	epic,
	startDate,
	dueDate,
	totalIssues,
	onDetailClick,
	onViewDetailsClick,
	onCreateIssueClick,
	onRefetch,
}: EpicCardContentProps) => {
	const { isCompanyManaged } = usePanelData();

	return (
		<>
			{isCompanyManaged ? (
				<EpicCardContentsCMP
					isLoading={isLoading}
					hasError={hasError}
					progress={progress}
					estimation={estimation}
					epic={epic}
					onDetailClick={onDetailClick}
					onRefetch={onRefetch}
				/>
			) : (
				<EpicCardContentsTMP
					startDate={startDate}
					dueDate={dueDate}
					isLoading={isLoading}
					hasError={hasError}
					totalIssues={totalIssues}
					onRefetch={onRefetch}
				/>
			)}
			<EpicCardButtons
				isActive={isActive}
				epicId={epic.id}
				onViewDetailsClick={onViewDetailsClick}
				onCreateIssueClick={onCreateIssueClick}
			/>
		</>
	);
};

const cardButtonContainerStyles = xcss({
	marginTop: 'space.100',
});
